// Matches the database default and allowed range for users.market_value_rate (percent).
export const DEFAULT_MARKET_VALUE_RATE = 0;
export const MIN_MARKET_VALUE_RATE = -6;
export const MAX_MARKET_VALUE_RATE = 6;

// Signed two-decimal percent string like "-1.25" or "+3" -> number, or null when it isn't a valid rate in range.
export const parseMarketValueRate = (input: string): number | null => {
  if (!/^[+-]?\d+(\.\d{1,2})?$/.test(input.trim())) return null;
  const rate = Number(input);
  return rate >= MIN_MARKET_VALUE_RATE && rate <= MAX_MARKET_VALUE_RATE ? rate : null;
};

// Raises or lowers a value by the user's Market Value Rate (percent). Missing values stay missing.
export const applyMarketValueRate = (value: number | null | undefined, ratePercent: number): number | null | undefined => {
  if (value === null || value === undefined) return value;
  return value * (1 + ratePercent / 100);
};

// Returns a copy of the AVM result fields (estimated_value, estimated_dollar_per_sqft, potential_equity)
// that include the user's Market Value Rate. Potential equity is estimated value minus list price, so it moves
// by the same dollar amount as the estimated value. The other fields are left untouched. A 0% rate returns
// the original object.
export const applyMarketValueRateToResultFields = <T extends Record<string, any>>(fields: T | null | undefined, ratePercent: number): T | null | undefined => {
  if (!fields || !ratePercent) return fields;

  const adjusted: Record<string, any> = {
    ...fields,
    estimated_value: applyMarketValueRate(fields.estimated_value, ratePercent),
    estimated_dollar_per_sqft: applyMarketValueRate(fields.estimated_dollar_per_sqft, ratePercent),
  };

  if (fields.potential_equity !== null && fields.potential_equity !== undefined &&
      fields.estimated_value !== null && fields.estimated_value !== undefined) {
    adjusted.potential_equity = fields.potential_equity + (adjusted.estimated_value - fields.estimated_value);
  }

  return adjusted as T;
};

// Same as above for the { result, comps, ... } shape returned by the most-recent AVM results endpoint.
// Comps and the other valuation methods are left untouched.
export const applyMarketValueRateToAvmResult = <T extends { result?: any }>(avmResult: T | null, ratePercent: number): T | null => {
  if (!avmResult?.result || !ratePercent) return avmResult;

  return { ...avmResult, result: applyMarketValueRateToResultFields(avmResult.result, ratePercent) };
};

// "+3.5%" / "-2%" / "0%" for labels.
export const formatMarketValueRate = (ratePercent: number): string => {
  const sign = ratePercent > 0 ? '+' : '';
  return `${sign}${ratePercent}%`;
};
